import { Event } from 'services/events/types';
import { formatShortDueDate } from 'utils/date';
import { formatEntryDate, formatLoggedHours, latestRemainingTime } from './timeLog';

const IGNORED_TASK_DIFF_KEYS = new Set([
  '__v',
  '_id',
  'archivedAt',
  'cancelledAt',
  'completedAt',
  'createdAt',
  'id',
  'modifiedAt',
  'score',
  'userId',
]);

const TASK_FIELD_LABEL_KEYS: Record<string, string> = {
  assignee: 'assignee',
  cancelReason: 'cancelReason',
  description: 'description',
  details: 'details',
  dueAt: 'dueDate',
  estimatedCompletionTime: 'estimatedCompletionTime',
  importance: 'importance',
  remainingTime: 'timeLeft',
  timeEntries: 'timeLogged',
  isArchived: 'archive',
  parentTaskId: 'parentTask',
  projects: 'projects',
  recurrence: 'recurrence',
  startAt: 'startDate',
  status: 'status',
  type: 'type',
};

const TASK_HISTORY_FIELD_ORDER = [
  'description',
  'details',
  'status',
  'importance',
  'cancelReason',
  'assignee',
  'projects',
  'estimatedCompletionTime',
  'remainingTime',
  'timeEntries',
  'startAt',
  'dueAt',
  'parentTaskId',
  'recurrence',
  'isArchived',
  'type',
];

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface ProjectRef {
  id?: string;
  name?: string;
}

interface SummarizeTaskEventOptions {
  projectName?: (projectId: string) => string | undefined;
  userName?: (userId: string) => string | undefined;
}

interface HistoryTimeEntry {
  endTime: string;
  estimatedRemainingTime?: number | null;
  id: string;
  minutes?: number;
  startTime?: string;
  userId?: string;
}

const collectKeys = (obj: Record<string, unknown> | undefined, keys: Set<string>) => {
  if (!obj) {
    return;
  }
  Object.keys(obj).forEach(key => {
    if (!IGNORED_TASK_DIFF_KEYS.has(key)) {
      keys.add(key);
    }
  });
};

const getChangedTaskFieldKeys = (event: Event): string[] => {
  const keys = new Set<string>();
  collectKeys(event.diff, keys);
  collectKeys(event.detailedDiff?.added, keys);
  collectKeys(event.detailedDiff?.deleted, keys);
  collectKeys(event.detailedDiff?.updated, keys);

  return [...keys].sort((a, b) => {
    const aIndex = TASK_HISTORY_FIELD_ORDER.indexOf(a);
    const bIndex = TASK_HISTORY_FIELD_ORDER.indexOf(b);
    const aOrder = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
    const bOrder = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return a.localeCompare(b);
  });
};

const joinWithAnd = (items: string[], andWord: string): string => {
  if (items.length === 0) {
    return '';
  }
  if (items.length === 1) {
    return items[0];
  }
  if (items.length === 2) {
    return `${items[0]} ${andWord} ${items[1]}`;
  }
  return `${items.slice(0, -1).join(', ')} ${andWord} ${items[items.length - 1]}`;
};

const projectRefs = (value: unknown): ProjectRef[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is ProjectRef => Boolean(item) && typeof item === 'object');
};

const projectIds = (projects: ProjectRef[]): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  projects.forEach(project => {
    if (!project.id || seen.has(project.id)) {
      return;
    }
    seen.add(project.id);
    ids.push(project.id);
  });
  return ids;
};

type TaskDateField = 'dueAt' | 'startAt';

const readTaskDate = (event: Event, field: TaskDateField): unknown => {
  if (event.task && field in event.task) {
    return event.task[field];
  }
  if (event.diff && field in event.diff) {
    return event.diff[field];
  }
  if (event.detailedDiff?.updated && field in event.detailedDiff.updated) {
    return event.detailedDiff.updated[field];
  }
  if (event.detailedDiff?.added && field in event.detailedDiff.added) {
    return event.detailedDiff.added[field];
  }
  if (
    event.task
    && event.oldTask
    && field in event.oldTask
    && !(field in event.task)
  ) {
    return null;
  }
  if (event.detailedDiff?.deleted && field in event.detailedDiff.deleted) {
    return null;
  }
  return undefined;
};

const readRemainingTime = (event: Event): number | null | undefined => {
  if (event.task && 'remainingTime' in event.task) {
    return event.task.remainingTime;
  }
  if (event.diff && 'remainingTime' in event.diff) {
    return event.diff.remainingTime as number | null;
  }
  if (event.detailedDiff?.updated && 'remainingTime' in event.detailedDiff.updated) {
    return event.detailedDiff.updated.remainingTime as number | null;
  }
  if (event.detailedDiff?.added && 'remainingTime' in event.detailedDiff.added) {
    return event.detailedDiff.added.remainingTime as number | null;
  }
  if (
    event.task
    && event.oldTask
    && 'remainingTime' in event.oldTask
    && !('remainingTime' in event.task)
  ) {
    return null;
  }
  if (event.detailedDiff?.deleted && 'remainingTime' in event.detailedDiff.deleted) {
    return null;
  }
  return undefined;
};

const describeRemainingValue = (
  value: number | null | undefined,
  t: Translate,
): string | null => {
  if (value === null) {
    return t('taskHistoryTimeLeftCleared');
  }
  if (typeof value !== 'number') {
    return null;
  }
  return t('taskHistoryTimeLeftSet', {
    hours: formatLoggedHours(value),
  });
};

const describeRemainingTime = (event: Event, t: Translate): string | null => (
  describeRemainingValue(readRemainingTime(event), t)
);

const IMPORTANCE_LABEL_KEYS: Record<string, string> = {
  high: 'importanceHigh',
  low: 'importanceLow',
  medium: 'importanceMedium',
};

const readAssignee = (event: Event): unknown => {
  if (event.task && 'assignee' in event.task) {
    return event.task.assignee;
  }
  if (event.diff && 'assignee' in event.diff) {
    return event.diff.assignee;
  }
  if (event.detailedDiff?.updated && 'assignee' in event.detailedDiff.updated) {
    return event.detailedDiff.updated.assignee;
  }
  if (event.detailedDiff?.added && 'assignee' in event.detailedDiff.added) {
    return event.detailedDiff.added.assignee;
  }
  if (
    event.task
    && event.oldTask
    && 'assignee' in event.oldTask
    && !('assignee' in event.task)
  ) {
    return null;
  }
  if (event.detailedDiff?.deleted && 'assignee' in event.detailedDiff.deleted) {
    return null;
  }
  return undefined;
};

const describeAssignee = (
  event: Event,
  t: Translate,
  userName?: (userId: string) => string | undefined,
): string | null => {
  const value = readAssignee(event);
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t('taskHistoryAssigneeCleared');
  }
  if (typeof value !== 'string') {
    return null;
  }
  return t('taskHistoryAssigneeSet', {
    assignee: userName?.(value) || t('unknownUser'),
  });
};

const readImportance = (event: Event): unknown => {
  if (event.task && 'importance' in event.task) {
    return event.task.importance;
  }
  if (event.diff && 'importance' in event.diff) {
    return event.diff.importance;
  }
  if (event.detailedDiff?.updated && 'importance' in event.detailedDiff.updated) {
    return event.detailedDiff.updated.importance;
  }
  if (event.detailedDiff?.added && 'importance' in event.detailedDiff.added) {
    return event.detailedDiff.added.importance;
  }
  if (
    event.task
    && event.oldTask
    && 'importance' in event.oldTask
    && !('importance' in event.task)
  ) {
    return null;
  }
  if (event.detailedDiff?.deleted && 'importance' in event.detailedDiff.deleted) {
    return null;
  }
  return undefined;
};

const describeImportance = (event: Event, t: Translate): string | null => {
  const value = readImportance(event);
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t('taskHistoryImportanceCleared');
  }
  const labelKey = IMPORTANCE_LABEL_KEYS[String(value)];
  if (!labelKey) {
    return null;
  }
  return t('taskHistoryImportanceSet', {
    importance: t(labelKey),
  });
};

const describeTaskDate = (
  event: Event,
  field: TaskDateField,
  t: Translate,
  setKey: string,
  clearedKey: string,
): string | null => {
  const value = readTaskDate(event, field);
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t(clearedKey);
  }
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return t(setKey, {
    date: formatShortDueDate(date),
  });
};

const nameForProject = (
  id: string,
  snapshots: ProjectRef[],
  projectName?: (projectId: string) => string | undefined,
) => snapshots.find(project => project.id === id)?.name || projectName?.(id) || id;

const describeProjectChanges = (
  event: Event,
  t: Translate,
  projectName?: (projectId: string) => string | undefined,
): string[] | null => {
  const andWord = t('and');
  const oldProjects = projectRefs(event.oldTask?.projects);
  const newProjects = projectRefs(event.task?.projects);
  const snapshots = [...newProjects, ...oldProjects];

  if (event.oldTask || event.task) {
    if (!event.oldTask || !event.task) {
      return null;
    }
    const oldIds = new Set(projectIds(oldProjects));
    const newIds = projectIds(newProjects);
    const assigned = newIds.filter(id => !oldIds.has(id));
    const unassigned = projectIds(oldProjects).filter(id => !newIds.includes(id));
    if (assigned.length === 0 && unassigned.length === 0) {
      return null;
    }
    const phrases: string[] = [];
    if (assigned.length > 0) {
      phrases.push(t('taskHistoryProjectsAssigned', {
        count: assigned.length,
        projects: joinWithAnd(assigned.map(id => nameForProject(id, snapshots, projectName)), andWord),
      }));
    }
    if (unassigned.length > 0) {
      phrases.push(t('taskHistoryProjectsUnassigned', {
        count: unassigned.length,
        projects: joinWithAnd(unassigned.map(id => nameForProject(id, snapshots, projectName)), andWord),
      }));
    }
    return phrases;
  }

  const added = projectRefs(event.diff?.projects ?? event.detailedDiff?.updated?.projects);
  const addedIds = projectIds(added);
  if (addedIds.length === 0) {
    return null;
  }
  return [t('taskHistoryProjectsAssigned', {
    count: addedIds.length,
    projects: joinWithAnd(addedIds.map(id => nameForProject(id, added, projectName)), andWord),
  })];
};

const timeEntriesOf = (value: unknown): HistoryTimeEntry[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap(item => {
    if (!item || typeof item !== 'object') {
      return [];
    }
    const entry = item as Partial<HistoryTimeEntry>;
    const estimatedRemainingTime = entry.estimatedRemainingTime === null || typeof entry.estimatedRemainingTime === 'number'
      ? entry.estimatedRemainingTime
      : undefined;
    const hasMinutes = typeof entry.minutes === 'number';
    if (!entry.id || !entry.endTime || (!hasMinutes && estimatedRemainingTime === undefined)) {
      return [];
    }
    const startTime = typeof entry.startTime === 'string' ? entry.startTime : undefined;
    return [{
      endTime: entry.endTime,
      id: entry.id,
      ...(hasMinutes ? { minutes: entry.minutes } : {}),
      ...(estimatedRemainingTime !== undefined ? { estimatedRemainingTime } : {}),
      ...(startTime ? { startTime } : {}),
      userId: entry.userId,
    }];
  });
};

const loggedTimePhrase = (
  entry: HistoryTimeEntry,
  t: Translate,
  userName?: (userId: string) => string | undefined,
  removed = false,
) => {
  const user = (entry.userId && userName?.(entry.userId)) || t('unknownUser');
  const hours = formatLoggedHours(entry.minutes ?? 0);
  if (entry.startTime && entry.endTime) {
    return t(removed ? 'taskHistoryTimeRemovedRange' : 'taskHistoryTimeLoggedRange', {
      end: formatEntryDate(entry.endTime),
      hours,
      start: formatEntryDate(entry.startTime),
      user,
    });
  }
  return t(removed ? 'taskHistoryTimeRemoved' : 'taskHistoryTimeLogged', {
    date: formatEntryDate(entry.endTime),
    hours,
    user,
  });
};

const describeTimeEntryChanges = (
  event: Event,
  t: Translate,
  userName?: (userId: string) => string | undefined,
): string[] | null => {
  if (!event.oldTask && !event.task) {
    return null;
  }
  const previous = timeEntriesOf(event.oldTask?.timeEntries);
  const next = timeEntriesOf(event.task?.timeEntries);
  const previousById = new Map(previous.map(entry => [entry.id, entry]));
  const nextIds = new Set(next.map(entry => entry.id));
  const fromEntries = latestRemainingTime(previous);
  let previousRemaining = fromEntries;
  if (previousRemaining === undefined && event.oldTask?.remainingTime !== undefined) {
    previousRemaining = event.oldTask.remainingTime;
  } else if (previousRemaining === undefined) {
    previousRemaining = event.oldTask?.estimatedCompletionTime;
  }
  const phrases: string[] = [];
  const remainingPhrase = (
    before: number | null | undefined,
    after: number | null | undefined,
  ) => (
    after === undefined || after === before ? null : describeRemainingValue(after, t)
  );
  next.forEach(entry => {
    const prior = previousById.get(entry.id);
    if (!prior) {
      if (typeof entry.minutes === 'number') {
        phrases.push(loggedTimePhrase(entry, t, userName));
      }
      const timeLeft = remainingPhrase(previousRemaining, entry.estimatedRemainingTime);
      if (timeLeft) {
        phrases.push(timeLeft);
      }
      return;
    }
    const timeLeft = remainingPhrase(prior.estimatedRemainingTime, entry.estimatedRemainingTime);
    if (timeLeft) {
      phrases.push(timeLeft);
    }
  });
  previous.forEach(entry => {
    if (nextIds.has(entry.id)) {
      return;
    }
    if (typeof entry.minutes !== 'number') {
      return;
    }
    phrases.push(loggedTimePhrase(entry, t, userName, true));
  });
  return phrases;
};

const summarizeTaskEvent = (
  event: Event,
  t: Translate,
  options?: SummarizeTaskEventOptions,
): string => {
  if (event.eventType === 'CREATED') {
    return t('taskCreated');
  }
  if (event.eventType === 'DELETED') {
    return t('taskDeleted');
  }

  const fieldKeys = getChangedTaskFieldKeys(event);
  if (fieldKeys.length === 0) {
    return t('taskUpdated');
  }

  const labels: string[] = [];
  const details: string[] = [];
  fieldKeys.forEach(key => {
    if (key === 'assignee') {
      const assignee = describeAssignee(event, t, options?.userName);
      if (assignee) {
        details.push(assignee);
        return;
      }
    }
    if (key === 'importance') {
      const importance = describeImportance(event, t);
      if (importance) {
        details.push(importance);
        return;
      }
    }
    if (key === 'startAt') {
      const startDate = describeTaskDate(
        event,
        'startAt',
        t,
        'taskHistoryStartDateSet',
        'taskHistoryStartDateCleared',
      );
      if (startDate) {
        details.push(startDate);
        return;
      }
    }
    if (key === 'dueAt') {
      const dueDate = describeTaskDate(
        event,
        'dueAt',
        t,
        'taskHistoryDueDateSet',
        'taskHistoryDueDateCleared',
      );
      if (dueDate) {
        details.push(dueDate);
        return;
      }
    }
    if (key === 'projects') {
      const projectChanges = describeProjectChanges(event, t, options?.projectName);
      if (projectChanges) {
        details.push(...projectChanges);
        return;
      }
    }
    if (key === 'estimatedCompletionTime') {
      const movedOntoEntry = Boolean(
        event.task
        && event.oldTask
        && 'estimatedCompletionTime' in event.oldTask
        && !('estimatedCompletionTime' in event.task)
        && timeEntriesOf(event.task.timeEntries).some(
          entry => entry.estimatedRemainingTime !== undefined,
        ),
      );
      if (movedOntoEntry) {
        return;
      }
    }
    if (key === 'remainingTime') {
      const recordedOnEntry = timeEntriesOf(event.task?.timeEntries).some(
        entry => entry.estimatedRemainingTime !== undefined,
      );
      if (recordedOnEntry) {
        return;
      }
      const timeLeft = describeRemainingTime(event, t);
      if (timeLeft) {
        details.push(timeLeft);
        return;
      }
    }
    if (key === 'timeEntries') {
      const timeChanges = describeTimeEntryChanges(event, t, options?.userName);
      if (timeChanges) {
        details.push(...timeChanges);
        return;
      }
    }
    labels.push(t(TASK_FIELD_LABEL_KEYS[key] ?? key));
  });

  const parts = [
    ...(labels.length > 0
      ? [t('taskUpdatedFields', { fields: joinWithAnd(labels, t('and')) })]
      : []),
    ...details,
  ];
  if (parts.length === 0) {
    return t('taskUpdated');
  }
  return parts.join('. ');
};

export {
  getChangedTaskFieldKeys,
  summarizeTaskEvent,
};
